import type { UIStateAPI } from "@/types";
import { zCurrentLanguage } from "@/data/schemas";

interface I18nAdapterDeps {
  readonly store: any;
  readonly uiState: UIStateAPI;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
}

export const hydrateI18nFromSession = (
  deps: Pick<I18nAdapterDeps, "store" | "getPath">,
  screen: Record<string, unknown> | null | undefined
) => {
  if (!screen) return;
  const { store, getPath } = deps;
  const lPath = getPath("currentLanguage");
  if (!lPath) return;
  try {
    store.registerSchema(lPath, zCurrentLanguage);
  } catch {}
  if (typeof (screen as any).currentLanguage === "string") {
    store.set(lPath, (screen as any).currentLanguage as any, {
      source: "hydration",
    });
  }
};

export const registerI18nAdapter = (deps: I18nAdapterDeps): (() => void) => {
  const { store, uiState, eventBus, persist } = deps;
  const unsubscribes: (() => void)[] = [];

  // Debug toggles
  const readDebugEnabled = (): boolean => {
    try {
      const d =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof d !== "string") return !!(d as any)?.i18n || d === "*";
      const parts = d.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("i18n");
    } catch {
      return false;
    }
  };
  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (debugEnabled) {
      try {
        // eslint-disable-next-line no-console
        console.debug("[adapter:i18n]", ...args);
      } catch {}
    }
  };
  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        try {
          if (!payload) return;
          if (payload.name === "i18n") debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {}
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {}

  const unsubscribeStoreLanguage = store.subscribe(
    (path: string) => path.endsWith(".screenState.currentLanguage"),
    ({ value, source }: { value: unknown; source?: string }) => {
      const isHydration = source === "hydration";
      try {
        if (typeof value === "string") (uiState as any).setLanguage(value);
        debug("store→UI language", { value, source });
      } catch {}
      if (!isHydration && source !== "socket") persist();
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreLanguage);

  // UIState -> Store (currentLanguage)
  const unsubscribeUIStateChanged = eventBus.on(
    "ui-state-changed",
    (data: any) => {
      try {
        const lang = data?.state?.current_language;
        const lPath = (deps.getPath as any)("currentLanguage") as string | null;
        if (!lPath) return;
        if (typeof lang === "string") {
          try {
            store.registerSchema(lPath, zCurrentLanguage);
          } catch {}
          // eslint-disable-next-line no-console
          if (
            (globalThis as any)?.__debugAdapters === "*" ||
            (globalThis as any)?.__debugAdapters?.includes?.("i18n")
          ) {
            try {
              console.debug("[adapter:i18n] UI→store currentLanguage", {
                lang,
              });
            } catch {}
          }
          store.set(lPath, lang as any, { source: "i18nAdapter" as any });
        }
      } catch {}
    }
  );
  unsubscribes.push(unsubscribeUIStateChanged);

  const cleanup = () => {
    for (const u of unsubscribes) {
      try {
        u();
      } catch {}
    }
  };
  try {
    const unsub = eventBus.on("app:destroy", () => cleanup());
    unsubscribes.push(unsub);
  } catch {}
  return cleanup;
};
